import { Input, Gap, Modal, Span, Alert } from '@salecycle/ui-component-library';
import { useFeatureFlags } from 'core/app/AppContext';
import { LD_EMAIL_TRACKING_CONSENT_FLAG } from 'core/constants/EmailTrackingUnsubscribeSettings';
import useNotifications from 'core/notifications/useNotifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTemplateEditorDispatch, useTemplateEditorState } from '../../context/UseTemplateEditorContext';
import { TemplateTypes } from '../../../template-list/constants/templateTypes';
import { useTemplateEditorSaveModal } from './TemplateEditorSaveModal';

interface IProps {
  commentModalOpen: boolean;
  setCommentModalOpen(isOpen: boolean): void;
}

export const TemplateEditorSaveCommentModal = ({ commentModalOpen, setCommentModalOpen }: IProps) => {
  const { t } = useTranslation(['designs', 'trackingLinks']);
  const [comment, setComment] = useState('');
  const { template } = useTemplateEditorState();
  const { showNotification } = useNotifications();
  const dispatch = useTemplateEditorDispatch();
  const { saveModal } = useTemplateEditorSaveModal();
  const { flagEnabled } = useFeatureFlags();
  const emailTrackingFeatureEnabled = flagEnabled(LD_EMAIL_TRACKING_CONSENT_FLAG);
  const globalSiteId = import.meta.env.VITE_GLOBAL_SITE_ID;

  const handleClose = () => {
    setCommentModalOpen(false);
    setComment('');
  };

  const handleConfirm = () => {
    saveModal(
      comment,
      () => {
        showNotification(t('designs:designGallery.form.comment.notifications.saveTemplateSuccess'), 'normal');
        dispatch({
          type: 'SAVE_TEMPLATE',
        });
      },
      () => {
        showNotification(t('designs:designGallery.form.comment.notifications.saveTemplateError'), 'error');
      },
    );
    setComment('');
    setCommentModalOpen(false);
  };

  return (
    <Modal
      heading={t('designs:designGallery.form.comment.heading')}
      variation="clean"
      isOpen={commentModalOpen}
      onCancelButtonClicked={handleClose}
      onCloseButtonClicked={handleClose}
      onOkButtonClicked={handleConfirm}
      okButtonLabel={t('trackingLinks:importSessionModal.confirm')}
      okButtonDisabled={comment === undefined || comment.length <= 0}
      cancelButtonLabel={t('trackingLinks:importSessionModal.cancel')}
    >
      <Span>{t('designs:designGallery.form.comment.description')}</Span>
      <Gap bottom={10} />

      <Input
        label={t('designs:designGallery.form.comment.comment')}
        ariaLabel={t('designs:designGallery.form.comment.comment')}
        hint={t('designs:designGallery.form.comment.commentHint')}
        placeholder={t('designs:designGallery.form.comment.commentPlaceholder')}
        onChange={newComment => setComment(newComment)}
        value={comment}
      />
      {template.siteId === globalSiteId && template.type === TemplateTypes.unsubscribe && (
        <div>
          <Alert heading={t('designs:designGallery.globalUnsubscribeHeader')} variation="warning">
            <p>{t('designs:designGallery.globalUnsubscribeWarning.globalUnsubscribeEditWarning')}</p>
          </Alert>
        </div>
      )}
      {emailTrackingFeatureEnabled &&
        template.siteId === globalSiteId &&
        template.type === TemplateTypes.unsubscribeEmailTracking && (
          <div>
            <Alert heading={t('designs:designGallery.globalEmailTrackingUnsubscribeHeader')} variation="warning">
              <p>
                {t(
                  'designs:designGallery.globalEmailTrackingUnsubscribeWarning.globalEmailTrackingUnsubscribeEditWarning',
                )}
              </p>
            </Alert>
          </div>
        )}
    </Modal>
  );
};
